import React, { useMemo, useState } from 'react';
import {
  Box,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from '@mui/material';

export const shareCardSx = {
  p: 2.5,
  bgcolor: 'rgba(var(--color-text-rgb), 0.03)',
  border: '1px solid rgba(var(--color-text-rgb), 0.08)',
  borderRadius: 2,
  minWidth: 0,
} as const;

/** Segmented control look shared by every toggle on the tab. */
export const segmentedToggleSx = {
  '& .MuiToggleButton-root': {
    textTransform: 'none',
    fontWeight: 600,
    px: 1.5,
    py: 0.5,
    fontSize: '0.8rem',
    borderColor: 'rgba(var(--color-text-rgb), 0.16)',
    color: 'rgba(var(--color-text-rgb), 0.72)',
    '&.Mui-selected': {
      bgcolor: 'rgba(var(--color-accent-rgb), 0.16)',
      color: 'var(--color-accent)',
      '&:hover': { bgcolor: 'rgba(var(--color-accent-rgb), 0.22)' },
    },
  },
} as const;

export const overlineSx = {
  color: 'rgba(var(--color-text-rgb), 0.6)',
  fontWeight: 700,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  fontSize: '0.68rem',
} as const;

// --- Tooltip ------------------------------------------------------------------

export const ShareTooltipShell: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <Paper
    elevation={0}
    sx={{
      px: 1.5,
      py: 1.25,
      minWidth: 200,
      borderRadius: 1.5,
      bgcolor: 'var(--color-surface-elevated)',
      border: '1px solid rgba(var(--color-text-rgb), 0.12)',
      boxShadow: '0 12px 28px rgba(0,0,0,0.28)',
    }}
  >
    <Typography variant="caption" sx={{ display: 'block', color: 'rgba(var(--color-text-rgb), 0.65)', mb: 0.75 }}>
      {title}
    </Typography>
    <Stack spacing={0.5}>{children}</Stack>
  </Paper>
);

/** Value first, label second; a short line key carries the series colour. */
export const ShareTooltipRow: React.FC<{ label: string; value: string; color?: string; strong?: boolean }> = ({
  label,
  value,
  color,
  strong,
}) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
    <Box sx={{ width: 12, height: 2, borderRadius: 1, flexShrink: 0, bgcolor: color ?? 'transparent' }} />
    <Typography
      variant={strong ? 'body1' : 'body2'}
      sx={{ fontWeight: strong ? 700 : 600, color: 'var(--color-text-strong)', fontVariantNumeric: 'tabular-nums' }}
    >
      {value}
    </Typography>
    <Typography variant="caption" sx={{ color: 'rgba(var(--color-text-rgb), 0.65)' }}>{label}</Typography>
  </Box>
);

export const LegendKey: React.FC<{ color: string; label: string }> = ({ color, label }) => (
  <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
    <Box sx={{ width: 10, height: 10, borderRadius: '3px', bgcolor: color }} />
    <Typography variant="caption" sx={{ color: 'rgba(var(--color-text-rgb), 0.75)', fontWeight: 600 }}>{label}</Typography>
  </Box>
);

// --- Sortable table -----------------------------------------------------------

export interface ShareTableColumn<T> {
  key: string;
  label: React.ReactNode;
  align?: 'left' | 'right';
  /** Sort key; omit to make the column unsortable. */
  sortValue?: (row: T) => number | string;
  render: (row: T) => React.ReactNode;
  /** Draws a thin bar under the value, scaled to the column's max. */
  meter?: { value: (row: T) => number; color: string };
  width?: number | string;
}

interface ShareSortableTableProps<T> {
  rows: T[];
  columns: ShareTableColumn<T>[];
  rowKey: (row: T) => string;
  defaultSort: { key: string; dir: 'asc' | 'desc' };
  maxHeight?: number;
  emptyText: string;
  onRowClick?: (row: T) => void;
  selectedKey?: string | null;
}

export function ShareSortableTable<T>({
  rows,
  columns,
  rowKey,
  defaultSort,
  maxHeight = 360,
  emptyText,
  onRowClick,
  selectedKey,
}: ShareSortableTableProps<T>): React.ReactElement {
  const [sort, setSort] = useState(defaultSort);

  const sorted = useMemo(() => {
    const column = columns.find((c) => c.key === sort.key);
    if (!column?.sortValue) return rows;
    const value = column.sortValue;
    const dir = sort.dir === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
      const av = value(a);
      const bv = value(b);
      if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * dir;
      return String(av).localeCompare(String(bv)) * dir;
    });
  }, [columns, rows, sort]);

  const meterMax = useMemo(() => {
    const out = new Map<string, number>();
    columns.forEach((c) => {
      if (c.meter) out.set(c.key, Math.max(0, ...rows.map((r) => c.meter!.value(r))));
    });
    return out;
  }, [columns, rows]);

  const headSx = {
    bgcolor: 'var(--color-surface)',
    fontWeight: 700,
    fontSize: '0.75rem',
    color: 'rgba(var(--color-text-rgb), 0.7)',
    whiteSpace: 'nowrap',
    borderBottom: '1px solid rgba(var(--color-text-rgb), 0.1)',
  } as const;

  return (
    <TableContainer sx={{ maxHeight, borderRadius: 1.5, border: '1px solid rgba(var(--color-text-rgb), 0.08)' }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            {columns.map((c) => (
              <TableCell key={c.key} align={c.align} sx={{ ...headSx, width: c.width }}>
                {c.sortValue ? (
                  <TableSortLabel
                    active={sort.key === c.key}
                    direction={sort.key === c.key ? sort.dir : 'desc'}
                    onClick={() => setSort((s) => (
                      s.key === c.key ? { key: c.key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key: c.key, dir: 'desc' }
                    ))}
                  >
                    {c.label}
                  </TableSortLabel>
                ) : c.label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {sorted.length === 0 ? (
            <TableRow>
              <TableCell colSpan={columns.length} sx={{ py: 4, textAlign: 'center', color: 'rgba(var(--color-text-rgb), 0.55)' }}>
                {emptyText}
              </TableCell>
            </TableRow>
          ) : sorted.map((row) => {
            const key = rowKey(row);
            const selected = selectedKey === key;
            return (
              <TableRow
                key={key}
                hover
                selected={selected}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                sx={{
                  cursor: onRowClick ? 'pointer' : undefined,
                  '&.Mui-selected, &.Mui-selected:hover': { bgcolor: 'rgba(var(--color-accent-rgb), 0.1)' },
                  '& td': { borderBottom: '1px solid rgba(var(--color-text-rgb), 0.06)' },
                }}
              >
                {columns.map((c) => {
                  const max = meterMax.get(c.key) ?? 0;
                  const meterValue = c.meter ? c.meter.value(row) : 0;
                  return (
                    <TableCell
                      key={c.key}
                      align={c.align}
                      sx={{ fontVariantNumeric: c.align === 'right' ? 'tabular-nums' : undefined, verticalAlign: 'middle' }}
                    >
                      {c.render(row)}
                      {c.meter ? (
                        <Box sx={{ mt: 0.5, height: 3, borderRadius: 2, bgcolor: 'rgba(var(--color-text-rgb), 0.06)', overflow: 'hidden' }}>
                          <Box
                            sx={{
                              height: '100%',
                              width: `${max > 0 ? (meterValue / max) * 100 : 0}%`,
                              bgcolor: c.meter.color,
                              borderRadius: 2,
                              ml: c.align === 'right' ? 'auto' : 0,
                            }}
                          />
                        </Box>
                      ) : null}
                    </TableCell>
                  );
                })}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
